<template>
  <VueJsModalPrimary
    v-bind="attrs.modal"
    :name="name"
    @before-close="beforeModalClose"
    @opened="onOpened"
  >
    <div :style="style.container">
      <HeaderLargePrimary display="block">
        <TextTruncate maxWidth="432px">
          Invite people to {{ tenantName }}
        </TextTruncate>
      </HeaderLargePrimary>
      <template v-if="!sentInvitations && !isSendingInvitations">
        <div :style="style.innerContainer">
          <div
            v-click-outside.stop="e => addEmail(e)"
            @click.stop="e => addEmail(e)"
            :style="style.formTagsContainer"
          >
            <validation-observer ref="observer">
              <validation-provider
                name="email"
                ref="provider"
                rules="email|required"
              >
                <b-form-tags
                  v-model="emails"
                  :disabled="isSendingInvitations"
                  duplicate-tag-text="Duplicate email(s)"
                  :inputAttrs="{ zIndex: 1 }"
                  :limit="limit"
                  :limit-tags-text="limitTagsText"
                  name="email"
                  placeholder="Enter email addresses..."
                  ref="emailTagsForm"
                  remove-on-delete
                  :separator="separator"
                  :tag-validator="onValidate"
                  @tag-state="onTagState"
                />
              </validation-provider>
            </validation-observer>
          </div>
          <div :style="style.errorMessage">
            {{ errors[0] }}
          </div>
        </div>
        <div :style="style.buttonsContainer">
          <ButtonSecondary
            minWidth="80px"
            @click="cancelInvitations"
          >
            Cancel
          </ButtonSecondary>
          <ButtonPrimary
            :disabled="invalid || (emails.length === 0 && validEmail.length === 0)"
            minWidth="68px"
            @click="sendInvitations"
            :style="style.button"
          >
            Send
          </ButtonPrimary>
        </div>
      </template>
      <template v-else-if="isSendingInvitations">
        <template v-if="isSendingInvitations">
          <LoaderPrimary :loading="isSendingInvitations" />
        </template>
      </template>
      <template v-else-if="sentInvitations">
        <div :style="style.messageContainer">
          <div>
            Sent invitations.
          </div>
          <ButtonSecondary
            :style="style.backButton"
            @click="onBack"
          >
            Back
          </ButtonSecondary>
        </div>
      </template>
    </div>
  </VueJsModalPrimary>
</template>

<script>
  import ApiTenantInvitationCode from '@js/api/tenant-invitation-code'
  import ButtonPrimary from '@modules/button/button-primary'
  import ButtonSecondary from '@modules/button/button-secondary'
  import ClickOutside from 'vue-click-outside'
  import HeaderLargePrimary from '@modules/text/header-large-primary'
  import HttpStatus from 'http-status-codes'
  import LoaderPrimary from '@modules/loader/loader-primary'
  import TextTruncate from '@modules/text/text-truncate'
  import VueJsModalPrimary from '@modules/v2/modal/vue-js-modal-primary'
  import { FontStyle, Layout } from '@js/styles'
  import { ValidationObserver, ValidationProvider } from 'vee-validate'
  import { camelKeyToSnakeKey } from '@js/str-utils'
  import { email, required } from '@js/validators/rules'
  import { mapGetters, mapState } from 'vuex'
  import { MODAL_WINDOW_NAMES } from '@js/constants'
  import client from '@js/client'
  import resources from '@js/resources'

  export default {
    name: 'ModalTenantInvitationPrimary',
    directives: {
      ClickOutside,
    },
    components: {
      ButtonPrimary,
      ButtonSecondary,
      HeaderLargePrimary,
      LoaderPrimary,
      TextTruncate,
      ValidationObserver,
      ValidationProvider,
      VueJsModalPrimary,
    },
    data: function () {
      return {
        emails: [],
        errorMessageTimerId: null,
        errorMessagePeriod: 5000,
        errors: [],
        input: '',
        inputTrigger: false,
        invalid: false,
        invitedEmails: [],
        isSendingInvitations: false,
        limit: 50,
        limitTagsText: '',
        name: MODAL_WINDOW_NAMES.INVITE_TO_TENANT_PRIMARY,
        sentInvitations: false,
        separator: ' ,;',
        validEmail: '',
        attrs: {
          modal: {
            maxWidth: 480,
            minHeight: 348,
          },
        },
        style: {
          backButton: {
            marginTop: Layout.space.md,
            minWidth: '68px',
          },
          buttonsContainer: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
          },
          button: {
            marginLeft: Layout.space.md,
          },
          container: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between',
            padding: Layout.space.lg,
            width: '100%',
          },
          errorMessage: {
            fontSize: FontStyle.size.sm,
            fontWeight: FontStyle.weight.lg,
            padding: `${Layout.space.xs} 0 0 ${Layout.space.sm}`,
          },
          formTagsContainer: {
            minHeight: '128px',
            maxHeight: '128px',
            overflowY: 'scroll',
            zIndex: 10,
          },
          innerContainer: {
            borderRadius: Layout.space.sm,
            height: '128px',
            margin: '36px 0',
          },
          messageContainer: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
          },
        },
      };
    },
    created: function () {
      this.style.errorMessage.color = this.color.warning1();

      this.style.innerContainer = {
        ...this.style.innerContainer,
        backgroundColor: this.color.shadow1(),
        border: `solid ${this.color.dark5()}`,
        borderWidth: '1px',
      };
      this.limitTagsText = `Invitation can be sent up to ${this.limit} at one time.`;
    },
    computed: {
      ...mapGetters('colorTheme', ['color']),
      ...mapState({
        tenantDomain: state => state.tenant.domain,
        tenantName: state => state.tenant.name,
        tenantUserId: state => state.tenantUser.id,
      }),
    },
    methods: {
      addEmail: async function (e) {
        if (e.target.className === 'close b-form-tag-remove') {
          return;
        }

        if (this.validEmail.length === 0) {
          return;
        }

        this.$refs.provider.validate(this.validEmail).then(elem => {
          if (elem.valid) {
            this.$refs.emailTagsForm.newTag = '';
            this.emails.push(this.validEmail);
            this.validEmail = '';
            this.errors = [];
          } else {
            this.errors = ['email is not valid.'];
            clearTimeout(this.errorMessageTimerId);
            this.errorMessageTimerId = setTimeout(() => {
              this.errors = [];
            }, this.errorMessagePeriod);
          }
        }).catch(error => {
          console.error(error);
        });
      },
      beforeModalClose: function () {
        this.validEmail = '';
        this.emails = [];
      },
      cancelInvitations: function () {
        this.$modal.hide(this.name);
      },
      onBack: function () {
        this.sentInvitations = false;
        this.emails = [];
        this.validEmail = '';
      },
      onOpened: function () {
        this.isSendingInvitations = false;
        this.sentInvitations = false;

        this.$watch(() => {
          return this.$refs.observer.invalid;
        }, (val) => {
          this.invalid = val;
        });
        this.$watch(() => {
          return this.$refs.provider.errors;
        }, (val) => {
          this.errors = val;
          clearTimeout(this.errorMessageTimerId);
          this.errorMessageTimerId = setTimeout(() => {
            this.errors = [];
          }, this.errorMessagePeriod);
        });
      },
      onTagState: function (validTags, invalidTags, duplicateTags) {
        if (validTags ? validTags.length > 0 : false) {
          this.$refs.provider.validate(validTags[0]).then(elem => {
            if (elem.valid) {
              this.validEmail = validTags[0];
              this.errors = [];
            } else {
              this.validEmail = '';
              this.errors = [];
            }
          }).catch(error => {
            console.error(error);
          })
        }
      },
      onValidate: function (input) {
        this.input = input;
        this.inputTrigger = !this.inputTrigger;
        return true;
      },
      postTenantInvitationCodes: async function (emails) {
        try {
          const payload = {
            emails: emails,
          };
          const api = await new ApiTenantInvitationCode();
          const res = await api.post(payload);
        } catch (error) {
          console.error(error);
          throw error;
        }
      },
      sendInvitations: async function () {
        try {
          this.isSendingInvitations = true;
          /*
          const payload = this.emails.length > 0 ? this.emails.map(elem => {
            return camelKeyToSnakeKey({
              tenantUserId: this.tenantUserId,
              email: elem,
            });
          }) : [camelKeyToSnakeKey({
              tenantUserId: this.tenantUserId,
              email: this.validEmail,
          })];*/

          const emails = [
            ...this.emails,
            ...(
              this.validEmail && !this.emails.includes(this.validEmail)
                ? [this.validEmail]
                : []
            ),
          ];
          await this.postTenantInvitationCodes(emails);
          //this.invitedEmails = await this.emails;
          //const emails = payload.map(elem => elem.email);
          //console.log(emails, this.emails, this.invitedEmails, this.validEmail);

          //const res = await client.post(
          //    resources['tenantInvitationCodes'](this.tenantDomain), payload);
          this.isSendingInvitations = await false;
          this.sentInvitations = await true;
          this.emails = await [];
          this.errors = await [];
        } catch (error) {
          this.isSendingInvitations = false;
          this.sentInvitations = false;
          if (error.response.status === HttpStatus.UNPROCESSABLE_ENTITY) {
            this.$store.commit(
                'notification/setWarning',
                'Workspace reached the available limit of users.');
          }
          console.error(error);
        }
      },
    },
    watch: {
      inputTrigger: function (newVal, oldVal) {
        this.$refs.provider.validate(this.input).then(elem => {
          if (elem.valid) {
            this.validEmail = this.input;
            this.errors = [];
          } else {
            this.validEmail = '';
            this.errors = [];
          }
        }).catch(error => {
          console.error(error);
        })
      },
    },
  };
</script>
